import { Button } from "frontend-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink, faPlus, faXmark } from "@fortawesome/pro-solid-svg-icons";

import { CharacterCount } from "@dashboard/components/CharacterCount";
import type { TaskLink } from "@dashboard/api/task";

import type { PendingLink } from "./useTaskLinks";

const LINK_TITLE_MAX_LENGTH = 100;

function isValidUrl(value: string): boolean {
    try {
        const u = new URL(value);
        return u.protocol === "http:" || u.protocol === "https:";
    } catch {
        return false;
    }
}

export function RelatedLinksSection({
    links,
    onAdd,
    onRequestRemove,
    mutationError,
    disabled,
}: {
    links: TaskLink[];
    onAdd: (link: PendingLink) => void;
    onRequestRemove: (id: number) => void;
    mutationError?: string | null;
    disabled?: boolean;
}) {
    const { t } = useTranslation();
    const [showForm, setShowForm] = useState(false);
    const [title, setTitle] = useState("");
    const [url, setUrl] = useState("");
    const [urlError, setUrlError] = useState("");

    const handleSave = () => {
        if (!title.trim()) return;
        const trimmedUrl = url.trim();
        if (!isValidUrl(trimmedUrl)) {
            setUrlError(t("common.invalidUrl", "Ugyldig URL. Eksempel: https://example.com"));
            return;
        }
        setUrlError("");
        onAdd({ title: title.trim(), url: trimmedUrl });
        setTitle("");
        setUrl("");
        setShowForm(false);
    };

    const handleCancel = () => {
        setTitle("");
        setUrl("");
        setUrlError("");
        setShowForm(false);
    };

    return (
        <div className="w-full flex flex-col gap-2">
            <span className="text-label font-medium uppercase tracking-wider text-text-secondary">
                {t("common.relatedLinks")}
            </span>

            {showForm ? (
                <div className="flex flex-col gap-2 border border-compendia-input rounded-none p-3">
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-start">
                        <div className="w-full sm:w-0 sm:flex-1 flex flex-col gap-1">
                            <input
                                id="link-title"
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder={t("common.linkTitle")}
                                aria-label={t("common.linkTitle")}
                                maxLength={LINK_TITLE_MAX_LENGTH}
                                className="w-full border border-compendia-input bg-compendia-card text-compendia-foreground px-3 py-2 rounded-none text-sm"
                            />
                            <div className="flex justify-end">
                                <CharacterCount
                                    currentLength={title.length}
                                    maxLength={LINK_TITLE_MAX_LENGTH}
                                />
                            </div>
                        </div>
                        <input
                            id="link-url"
                            type="url"
                            value={url}
                            onChange={(e) => {
                                setUrl(e.target.value);
                                if (urlError) setUrlError("");
                            }}
                            placeholder={t("common.urlPlaceholder")}
                            aria-label={t("common.urlPlaceholder")}
                            className={`w-full sm:w-0 sm:flex-1 border bg-compendia-card text-compendia-foreground px-3 py-2 rounded-none text-sm ${
                                urlError
                                    ? "border-chameleon-pink ring-1 ring-chameleon-pink"
                                    : "border-compendia-input"
                            }`}
                        />
                    </div>
                    {urlError && <span className="text-chameleon-pink text-xs">{urlError}</span>}
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="submit"
                            size="submit"
                            onClick={handleSave}
                            disabled={disabled || !title.trim() || !url.trim()}
                        >
                            {t("common.save")}
                        </Button>
                        <Button type="button" variant="outline" onClick={handleCancel}>
                            {t("common.cancel")}
                        </Button>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    disabled={disabled}
                    className="flex items-center justify-center gap-1 w-full border border-dashed border-compendia-input rounded-none py-2 text-sm text-text-secondary hover:text-compendia-foreground hover:border-border-input-hover cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => setShowForm(true)}
                >
                    <FontAwesomeIcon icon={faPlus} className="text-xs" />
                    {t("common.addLink")}
                </button>
            )}

            {mutationError && <span className="text-chameleon-pink text-xs">{mutationError}</span>}

            {links.length > 0 && (
                <ul className="w-full flex flex-col gap-2">
                    {links.map((link) => (
                        <li
                            key={link.id}
                            className="w-full flex items-center gap-2 border border-compendia-input rounded-none px-3 py-2 overflow-hidden"
                        >
                            <FontAwesomeIcon icon={faLink} />
                            <div className="flex-1 overflow-hidden">
                                <p className="font-medium text-sm truncate">{link.title}</p>
                                <a
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block text-xs font-mono text-text-secondary hover:underline truncate"
                                >
                                    {link.url}
                                </a>
                            </div>
                            <button
                                type="button"
                                disabled={disabled}
                                aria-label={t("common.removeLink")}
                                className="text-text-secondary hover:text-compendia-foreground cursor-pointer shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => onRequestRemove(link.id)}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
